import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ResourceTypeLink } from "@evergis/api";
import { LinearProgress } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { LayerReferencesTooltip } from "../../DeleteLayer/LayerReferencesTooltip";
import { CheckboxDescription, TextLine } from "../../DeleteLayer/styled";
import { RequestLoader } from "../styled";

import { MAX_TOOLTIP_ITEMS_LENGTH } from "../../DeleteLayer/const";

import { layerRefsWithTables, LayerRefsWithTablesInput } from "../../../../resources/layers";
import { projectsRefsWithLayers, projectsRefsWithTables } from "../../../../resources/projects";

import { ResourcesResolved } from "../resolveResources";

export enum RequestInfoType {
  MapsWithLayers,
  MapsWithTables,
  LayersWithTables,
}

export interface RequestInfoProps extends ResourcesResolved {
  type: RequestInfoType;
  resources?: typeof layerRefsWithTables | typeof projectsRefsWithLayers | typeof projectsRefsWithTables;
  label?: string;
  unavailableText?: string;
  resourceNames?: string[];
}

const RequestInfoComponent: FC<RequestInfoProps> = ({ resources, label, unavailableText, resourceNames, type }) => {
  const [input, setInput] = useState<LayerRefsWithTablesInput>();
  const { data, loading } = resources.use({
    input,
    waitInput: true,
  });

  const onRequest = useCallback(() => {
    setInput({
      names: resourceNames,
      extendedRefsLimit: MAX_TOOLTIP_ITEMS_LENGTH,
    });
  }, [resourceNames]);

  const myRefsNames = useMemo(
    () =>
      !data
        ? []
        : data.my.map(ref => {
            if (typeof ref === "string") {
              return ref;
            }

            const { alias: refAlias, name: refName } = ref;

            return refAlias || refName;
          }),
    [data],
  );

  useEffect(() => {
    if (input === undefined) {
      onRequest();
    }
  }, []);

  return (
    <CheckboxDescription>
      <TextLine>
        <span>{label}&nbsp;</span>
        {loading && input !== undefined && (
          <RequestLoader>
            <LinearProgress />
          </RequestLoader>
        )}
        {!loading && data !== undefined && (data?.my.length > 0 || data?.others.length > 0) && (
          <LayerReferencesTooltip
            resourceType={type === RequestInfoType.LayersWithTables ? ResourceTypeLink.Layer : ResourceTypeLink.Project}
            references={myRefsNames}
            otherReferences={data.others}
          />
        )}
      </TextLine>
      <TextLine>
        <span>{unavailableText}</span>
      </TextLine>
    </CheckboxDescription>
  );
};

export const RequestDependencyInfo: FC<RequestInfoProps> = props => {
  const { t } = useTranslation("dialogs");
  const { layers, tables } = props;

  switch (props.type) {
    case RequestInfoType.MapsWithLayers:
      return (
        <RequestInfoComponent
          label={t("deleteResources.mapsWithLayers")}
          unavailableText={t("deleteResources.unavailableLayersCatalog")}
          resources={projectsRefsWithLayers}
          resourceNames={layers.map(({ name }) => name)}
          {...props}
        />
      );
    case RequestInfoType.MapsWithTables:
      return (
        <RequestInfoComponent
          label={t("deleteResources.mapsWithLayersDataSources")}
          unavailableText={t("deleteResources.unavailableDataSources")}
          resources={projectsRefsWithTables}
          resourceNames={tables.map(({ name }) => name)}
          {...props}
        />
      );
    case RequestInfoType.LayersWithTables:
      return (
        <RequestInfoComponent
          label={t("deleteResources.layersWithDataSources")}
          unavailableText={t("deleteResources.unavailableDataSources")}
          resources={layerRefsWithTables}
          resourceNames={tables.map(({ name }) => name)}
          {...props}
        />
      );
    default:
      return null;
  }
};
